import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { poolAPI, entryAPI, dataAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Check, ArrowRight, Users, Lock } from 'lucide-react';

export default function JoinPool() {
  const { poolId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pool, setPool] = useState(null);
  const [teams, setTeams] = useState([]);
  const [step, setStep] = useState('join'); // join | entries | pick
  const [teamAlias, setTeamAlias] = useState('');
  const [pin, setPin] = useState('');
  const [numEntries, setNumEntries] = useState(1);
  const [myEntries, setMyEntries] = useState([]);
  const [currentEntryIdx, setCurrentEntryIdx] = useState(0);
  const [picks, setPicks] = useState({});
  const [goalsPrediction, setGoalsPrediction] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const poolRes = await poolAPI.get(poolId);
        setPool(poolRes.data.pool);
        if (poolRes.data.pool.is_member) {
          setStep('entries');
          if (poolRes.data.pool.team_alias) setTeamAlias(poolRes.data.pool.team_alias);
        }
        if (poolRes.data.pool.season_id) {
          const teamsRes = await dataAPI.getTeams(poolRes.data.pool.season_id);
          setTeams(teamsRes.data.teams);
        }
        const entriesRes = await entryAPI.myEntries(poolId);
        setMyEntries(entriesRes.data.entries);
        const unpicked = entriesRes.data.entries.filter(e => e.status === 'pending' && (!e.picks || e.picks.length === 0));
        if (unpicked.length > 0) { setStep('pick'); setCurrentEntryIdx(0); }
      } catch (err) { console.error(err); }
    };
    load();
  }, [poolId]);

  const handleJoin = async () => {
    if (!pin || pin.length !== 4) { toast.error('Please enter the 4-digit Pool PIN'); return; }
    setLoading(true);
    try {
      await poolAPI.join(poolId, teamAlias, pin);
      const poolRes = await poolAPI.get(poolId);
      setPool(poolRes.data.pool);
      if (poolRes.data.pool.season_id) {
        const teamsRes = await dataAPI.getTeams(poolRes.data.pool.season_id);
        setTeams(teamsRes.data.teams);
      }
      setStep('entries');
      toast.success('Joined pool!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to join');
    } finally { setLoading(false); }
  };

  const handleCreateEntries = async () => {
    setLoading(true);
    try {
      const res = await entryAPI.create(poolId, numEntries);
      setMyEntries(prev => [...prev, ...res.data.entries]);
      toast.success(res.data.message);
      if (teams.length === 0 && pool?.season_id) {
        const teamsRes = await dataAPI.getTeams(pool.season_id);
        setTeams(teamsRes.data.teams);
      }
      setStep('pick');
      setCurrentEntryIdx(0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create entries');
    } finally { setLoading(false); }
  };

  const unpickedEntries = myEntries.filter(e => e.status === 'pending' && (!e.picks || e.picks.length === 0));
  const currentEntry = unpickedEntries[currentEntryIdx];

  const handleSubmitPicks = async () => {
    if (Object.keys(picks).length !== 5) { toast.error('Pick one team from each tier'); return; }
    if (!goalsPrediction || parseInt(goalsPrediction) < 1) { toast.error('Goals prediction is required (positive integer)'); return; }
    setLoading(true);
    try {
      const picksArr = Object.entries(picks).map(([tier, teamName]) => ({ tier: parseInt(tier), team_name: teamName }));
      await entryAPI.submitPicks(currentEntry.id, {
        picks: picksArr,
        goals_prediction: parseInt(goalsPrediction),
        display_name: teamAlias || user?.name || currentEntry.display_name,
      });
      toast.success(`Entry ${currentEntry.entry_number} picks submitted!`);
      setPicks({});
      setGoalsPrediction('');
      if (currentEntryIdx + 1 < unpickedEntries.length) {
        setCurrentEntryIdx(currentEntryIdx + 1);
      } else {
        navigate(`/pools/${poolId}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setLoading(false); }
  };

  const tierTeams = [1,2,3,4,5].map(tier => ({
    tier,
    teams: teams.filter(t => t.tier === tier).sort((a, b) => a.name.localeCompare(b.name)),
    mult: { 1: {w:2,d:1}, 2: {w:4,d:2}, 3: {w:6,d:3}, 4: {w:8,d:4}, 5: {w:10,d:5} }[tier],
  }));

  if (!pool) return <div className="animate-pulse h-96 bg-muted rounded-2xl" />;

  if (pool.status === 'locked') {
    return (
      <div className="max-w-lg mx-auto text-center py-16">
        <div className="h-16 w-16 rounded-full bg-yellow-100 mx-auto mb-4 flex items-center justify-center">
          <Lock className="h-8 w-8 text-yellow-600" />
        </div>
        <h2 className="font-heading text-xl font-bold mb-2">Pool is Locked</h2>
        <p className="text-muted-foreground text-sm mb-6">This pool is no longer accepting new entries or changes. The season is in progress.</p>
        <Button asChild variant="outline"><Link to={pool.is_member ? `/pools/${poolId}` : '/pools'}>
          {pool.is_member ? 'View Pool Standings' : 'Browse Other Pools'}
        </Link></Button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <h1 className="font-heading text-2xl font-bold mb-2">{pool.name}</h1>

      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-6">
        {['Join', 'Entries', 'Pick Teams'].map((label, i) => {
          const stepMap = ['join', 'entries', 'pick'];
          const isActive = stepMap[i] === step;
          const isDone = stepMap.indexOf(step) > i;
          return (
            <div key={label} className="flex items-center gap-2">
              <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold ${
                isDone ? 'bg-[#5b3cc4] text-white' : isActive ? 'bg-[#5b3cc4] text-white' : 'bg-muted text-muted-foreground'
              }`}>{isDone ? <Check className="h-4 w-4" /> : i + 1}</div>
              <span className={`text-sm font-medium ${isActive ? 'text-foreground' : 'text-muted-foreground'}`}>{label}</span>
              {i < 2 && <ArrowRight className="h-4 w-4 text-muted-foreground mx-1" />}
            </div>
          );
        })}
      </div>

      {/* STEP 1: Join */}
      {step === 'join' && (
        <Card>
          <CardHeader><CardTitle>Join This Pool</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="text-muted-foreground">Entry Fee:</span> <strong>{pool.organizer_fee || "Details provided by organizer"} (collected by organizer)</strong></div>
              <div><span className="text-muted-foreground">Max Entries:</span> <strong>{pool.max_entries_per_person || 2} per person</strong></div>
              <div><span className="text-muted-foreground">Deadline:</span> <strong>{pool.entry_deadline ? new Date(pool.entry_deadline).toLocaleDateString() : 'TBD'}</strong></div>
              <div><span className="text-muted-foreground">Plan:</span> <strong>{pool.plan_label || 'Free'} ({pool.plan_capacity || 8} max)</strong></div>
            </div>
            {pool.payment_email && (
              <p className="text-sm">Entry fee contributions should be sent to: <span className="font-medium text-[#5b3cc4]">{pool.payment_email}</span></p>
            )}
            <div>
              <Label>Team Name / Alias (shown on leaderboard)</Label>
              <Input value={teamAlias} onChange={(e) => setTeamAlias(e.target.value)} placeholder="e.g., DOMO TEMPLE BAR" />
              <p className="text-xs text-muted-foreground mt-1">Your real name will also be shown. This is optional.</p>
            </div>
            <div>
              <Label>Pool PIN (4 digits) *</Label>
              <Input type="text" maxLength={4} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))} placeholder="Enter 4-digit PIN" required />
              <p className="text-xs text-muted-foreground mt-1">Get the PIN from the pool organizer to join this pool.</p>
            </div>
            <Button onClick={handleJoin} disabled={loading || pin.length !== 4} className="w-full bg-[#5b3cc4] hover:bg-[#4e33a8]">{loading ? 'Joining...' : 'Join Pool'}</Button>
          </CardContent>
        </Card>
      )}

      {/* STEP 2: Create Entries */}
      {step === 'entries' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Users className="h-5 w-5" />Select Number of Entries</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Team Name / Alias (shown on leaderboard)</Label>
              <Input value={teamAlias} onChange={(e) => setTeamAlias(e.target.value)} placeholder="e.g., DOMO TEMPLE BAR" />
              <p className="text-xs text-muted-foreground mt-1">Your real name will also be shown. This is optional.</p>
              {teamAlias && teamAlias !== pool?.team_alias && (
                <Button variant="outline" size="sm" className="mt-2" onClick={async () => { try { await poolAPI.updateAlias(poolId, teamAlias); toast.success('Team name saved'); } catch(e) { toast.error('Failed'); } }}>Save Team Name</Button>
              )}
            </div>
            <p className="text-sm text-muted-foreground">Choose how many entries you'd like (max {pool.max_entries_per_person || 2}). You have {myEntries.length} entry(ies) so far.</p>
            <p className="text-sm font-medium text-[#5b3cc4] mt-1">Note: The pool organizer will be in touch to collect from you.</p>
            <div className="flex gap-3">
              {[1,2].filter(n => n + myEntries.length <= (pool.max_entries_per_person || 2)).map(n => (
                <button key={n} onClick={() => setNumEntries(n)}
                  className={`flex-1 rounded-xl border-2 p-4 text-center transition-all ${
                    numEntries === n ? 'border-[#5b3cc4] bg-[#f3f0ff]' : 'border-border hover:border-muted-foreground'
                  }`}>
                  <div className="font-heading text-2xl font-bold">{n}</div>
                  <div className="text-sm text-muted-foreground">{n === 1 ? 'entry' : 'entries'}</div>
                </button>
              ))}
            </div>
            {myEntries.length >= (pool.max_entries_per_person || 2) ? (
              <div className="text-center py-4">
                <p className="text-sm text-muted-foreground">You've reached the maximum entries.</p>
                <Button variant="outline" className="mt-2" onClick={() => navigate(`/pools/${poolId}`)}>View Pool</Button>
              </div>
            ) : (
              <Button onClick={handleCreateEntries} disabled={loading} size="lg" className="w-full bg-[#5b3cc4] hover:bg-[#4e33a8]">
                {loading ? 'Creating...' : `Create ${numEntries} Entry Slot${numEntries > 1 ? 's' : ''}`}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* STEP 3: Pick Teams */}
      {step === 'pick' && currentEntry && (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Pick Teams — Entry {currentEntry.entry_number}</CardTitle>
                <Badge variant="secondary">{currentEntryIdx + 1} of {unpickedEntries.length} to complete</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label>Goals Prediction (mandatory)</Label>
                <Input type="number" value={goalsPrediction} onChange={(e) => setGoalsPrediction(e.target.value)} placeholder="Total goals by all 20 EPL teams this season" required data-testid="entry-goals-prediction" />
                <p className="text-xs text-muted-foreground mt-1">Predict the total goals scored by ALL 20 EPL teams during the entire season. Used as tiebreaker.</p>
              </div>
            </CardContent>
          </Card>

          {tierTeams.map(({ tier, teams: tierList, mult }) => (
            <Card key={tier} data-testid={`entry-tier-${tier}-section`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Tier {tier}</CardTitle>
                  <span className="text-xs font-mono bg-secondary px-2 py-1 rounded">Win: {mult.w}x | Draw: {mult.d}x</span>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {tierList.map((team) => {
                    const selected = picks[tier] === team.name;
                    return (
                      <button key={team.id} type="button" onClick={() => setPicks(p => ({ ...p, [tier]: team.name }))}
                        data-testid={`entry-tier-${tier}-team-${team.code}`}
                        className={`relative rounded-xl border p-3 text-left transition-all hover:bg-muted/40 h-[72px] flex items-center ${
                          selected ? 'ring-2 ring-[#5b3cc4] bg-[#f3f0ff]/30 border-[#5b3cc4]' : 'bg-card'
                        }`}>
                        <div className="flex items-center gap-2 w-full">
                          <img src={team.logo} alt="" className="h-8 w-8 rounded flex-shrink-0" onError={(e) => { e.target.style.display='none'; }} />
                          <div className="min-w-0">
                            <div className="text-sm font-medium leading-tight truncate">{team.name}</div>
                            <div className="text-[10px] text-muted-foreground">{team.code}</div>
                          </div>
                        </div>
                        {selected && <div className="absolute top-1 right-1 bg-[#5b3cc4] text-white text-[9px] px-1.5 py-0.5 rounded-full">Picked</div>}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}

          <div className="sticky bottom-4 z-10">
            <Button onClick={handleSubmitPicks} size="lg" className="w-full shadow-lg bg-[#5b3cc4] hover:bg-[#4e33a8]" disabled={loading || Object.keys(picks).length !== 5 || !goalsPrediction}
              data-testid="entry-submit-button">
              {loading ? 'Submitting...' : `Submit Entry ${currentEntry.entry_number} (${Object.keys(picks).length}/5 picked)`}
            </Button>
          </div>
        </div>
      )}

      {step === 'pick' && !currentEntry && (
        <Card>
          <CardContent className="py-12 text-center">
            <Check className="h-12 w-12 mx-auto text-[#5b3cc4] mb-4" />
            <h3 className="font-heading text-lg font-semibold mb-2">All Entries Complete!</h3>
            <Button asChild className="bg-[#5b3cc4] hover:bg-[#4e33a8]"><Link to={`/pools/${poolId}`}>View Pool Standings</Link></Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
